import React, { useState, useCallback, useRef } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { Col } from "react-bootstrap";
import { GetServiceCodeDescription } from "../config/preauthorizationApi";
import { useDispatch } from "react-redux";

const DiagnosisCodeSelect = ({
  control,
  setValue,
  notLabel = false,
  watch,
  name = "diagnosis_code_discription",
  isAdd,
  preauthorizationDiagnosis,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const debounceTimeout = useRef(null);

  const isValidOption = (item) => {
    const today = new Date();
    const effectiveFrom = item.effective_from
      ? new Date(item.effective_from)
      : null;
    const inactive = item.inactive ? new Date(item.inactive) : null;
    const reactivated = item.reactivated ? new Date(item.reactivated) : null;

    // Condition 1: If valid is 0, filter out (don't show in dropdown)
    if (item.valid === 0) {
      return false;
    }

    // Condition 2: If valid is 1 and effective_from exists, is before today,
    // and inactive and reactivated do not exist, it's valid
    if (
      item.valid === 1 &&
      effectiveFrom &&
      effectiveFrom <= today &&
      !inactive &&
      !reactivated
    ) {
      return true;
    }

    // Condition 3: If valid is 1, effective_from exists and is before today,
    // inactive exists and is after today, and reactivated is null, it's valid
    if (
      item.valid === 1 &&
      effectiveFrom &&
      effectiveFrom <= today &&
      inactive &&
      inactive > today &&
      !reactivated
    ) {
      return true;
    }

    // Condition 4: If effective_from exists and is before today,
    // inactive exists and is before today, and reactivated exists and is before today, it's valid
    if (
      effectiveFrom &&
      effectiveFrom <= today &&
      inactive &&
      inactive <= today &&
      reactivated &&
      reactivated <= today
    ) {
      return true;
    }

    // Condition 5: If valid is 1 and effective_from, inactive, and reactivated do not exist, it's valid
    if (item.valid === 1 && !effectiveFrom && !inactive && !reactivated) {
      return true;
    }

    // Corrected Condition 6: If valid is 1, effective_from is before today,
    // inactive is before today, and reactivated is after today, it's valid
    if (
      item.valid === 1 &&
      effectiveFrom &&
      effectiveFrom <= today &&
      inactive &&
      inactive <= today &&
      reactivated &&
      reactivated < today
    ) {
      return true;
    }

    return false;
  };

  const loadOptions = useCallback(
    (inputValue, callback) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      if (!inputValue) {
        callback([]);
        return;
      }

      debounceTimeout.current = setTimeout(async () => {
        try {
          setLoading(true);
          const response = await dispatch(
            GetServiceCodeDescription({
              diag_code: inputValue,
            })
          ).unwrap();

          const options = response.data.Diagnosis_code.filter((item) =>
            isValidOption(item)
          ).map((item) => ({
            value: item.icd_code,
            label: item.icd_description,
            level: item?.level,
            valid: item?.valid,
            effective_from: item?.effective_from,
            inactive: item?.inactive,
            reactivated: item?.reactivated,
            sex: item?.sex,
            Stype: item?.Stype,
            AgeL: item?.AgeL,
            AgeH: item?.AgeH,
            Atype: item?.Atype,
            RDiag: item?.RDiag,
            Morph_Code: item?.Morph_Code,
            RTA_diag: item?.RTA_diag,
            concept_change: item?.concept_change,
            UnacceptPDx: item?.UnacceptPDx,
          }));

          callback(options);
        } catch (error) {
          console.error("Error fetching diagnosis codes:", error);
          callback([]);
        } finally {
          setLoading(false);
        }
      }, 500);
    },
    [dispatch]
  );

  return (
    <Col xs={10}>
      {name === "diagnosis_code_discription" && !notLabel && (
        <label className="form-label">Diagnosis Code / Description</label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{ required: false }}
        render={({ field }) => (
          <AsyncSelect
            {...field}
            isClearable
            defaultOptions
            loadOptions={loadOptions}
            size="sm"
            isLoading={loading}
            placeholder="Search diagnosis codes..."
            classNamePrefix="react-select"
            menuPlacement="auto"
            value={
              field.value
                ? {
                    value: field.value.icd_code,
                    label: field.value.icd_description,
                    level: field?.value?.level,
                    effective_from: field?.value?.effective_from,
                    inactive: field?.value?.inactive,
                    reactivated: field?.value?.reactivated,
                    sex: field?.value?.sex,
                    Stype: field?.value?.Stype,
                    AgeL: field?.value?.AgeL,
                    AgeH: field?.value?.AgeH,
                    Atype: field?.value?.Atype,
                    RDiag: field?.value?.RDiag,
                    Morph_Code: field?.value?.Morph_Code,
                    RTA_diag: field?.value?.RTA_diag,
                    concept_change: field?.value?.concept_change,
                    UnacceptPDx: field?.value?.UnacceptPDx,
                  }
                : null
            }
            onChange={(selected) => {
              setValue(
                name,
                selected
                  ? {
                      icd_code: selected.value,
                      icd_description: selected.label,
                      level: selected?.level,
                      effective_from: selected?.effective_from,
                      inactive: selected?.inactive,
                      reactivated: selected?.reactivated,
                      sex: selected?.sex,
                      Stype: selected?.Stype,
                      AgeL: selected?.AgeL,
                      AgeH: selected?.AgeH,
                      Atype: selected?.Atype,
                      RDiag: selected?.RDiag,
                      Morph_Code: selected?.Morph_Code,
                      RTA_diag: selected?.RTA_diag,
                      concept_change: selected?.concept_change,
                      UnacceptPDx: selected?.UnacceptPDx,
                    }
                  : null
              );
            }}
            styles={customSmStyles}
            formatOptionLabel={({ label, value }) => (
              <div>
                ({value}) {label}
              </div>
            )}
          />
        )}
      />
    </Col>
  );
};

export default DiagnosisCodeSelect;

const customSmStyles = {
  control: (base) => ({
    ...base,
    minHeight: "29px",
    fontSize: "0.875rem",
    backgroundColor: "var(--falcon-input-bg)",
    borderColor: "var(--falcon-input-border-color)",
    "&:hover": {
      borderColor: "var(--falcon-input-focus-border-color)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    color: "var(--falcon-input-color)",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "var(--falcon-input-color) !important",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "29px",
    minHeight: "29px",
    svg: {
      color: "var(--falcon-input-color) !important",
    },
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    paddingTop: 8,
    paddingBottom: 0,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: "0.8rem",
    minHeight: "30px",
    lineHeight: "1.2",
  }),
  menu: (base) => ({
    ...base,
    marginTop: 0,
    borderRadius: 0,
    backgroundColor: "var(--falcon-card-bg)",
    border: "1px solid var(--falcon-border-color)",
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#fff",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--falcon-body-color)",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--falcon-body-color)",
    "&:hover": {
      backgroundColor: "var(--falcon-danger)",
      color: "var(--falcon-white)",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--falcon-input-placeholder-color)",
    margin: 0,
    padding: 0,
  }),
};
